import React, { useState } from "react";
import { db } from "./firebase";
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  onSnapshot, 
  doc 
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import LiveChat from "./LiveChat";

export default function ChatRequestModal({ user, onClose }) {
  const [step, setStep] = useState("select"); // select | searching | active
  const [category, setCategory] = useState("");
  const [sessionId, setSessionId] = useState(null);

  const categories = [
    { id: "anxiety", label: "Anxiety", icon: "😟" },
    { id: "depression", label: "Depression", icon: "😔" },
    { id: "stress", label: "Stress", icon: "😫" },
    { id: "relationship", label: "Relationship", icon: "👫" },
    { id: "other", label: "Other", icon: "💬" }
  ];

  const handleRequest = async (catId) => {
    setCategory(catId);
    setStep("searching");

    try {
      const docRef = await addDoc(collection(db, "chat_sessions"), {
        userId: user.uid,
        userName: user.displayName || user.email.split('@')[0],
        category: catId,
        status: "pending",
        createdAt: serverTimestamp(),
        duration: 1800 // 30 mins
      });

      setSessionId(docRef.id);

      // Listen for status change to "active"
      const unsubscribe = onSnapshot(doc(db, "chat_sessions", docRef.id), (docSnap) => {
        if (docSnap.exists() && docSnap.data().status === "active") {
          setStep("active");
          unsubscribe();
        }
      });

    } catch (err) {
      console.error("Error creating request:", err);
      alert("Failed to create request. Please try again.");
      setStep("select");
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      style={styles.overlay}
    >
      <motion.div 
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        style={styles.modal}
      >
        <button onClick={onClose} style={styles.closeBtn}>✕</button>

        {step === "select" && (
          <div style={styles.content}>
            <h2 style={styles.title}>What's on your mind?</h2>
            <p style={styles.subtitle}>Select a category to be matched with an available therapist instantly.</p>
            
            <div style={styles.grid}>
              {categories.map((cat) => (
                <button 
                  key={cat.id} 
                  style={styles.catCard}
                  onClick={() => handleRequest(cat.id)}
                >
                  <span style={styles.catIcon}>{cat.icon}</span>
                  <span style={styles.catLabel}>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "searching" && (
          <div style={styles.content}>
            <div style={styles.loaderWrapper}>
              <div style={styles.pulseRing} />
              <div style={styles.loaderIcon}>🔍</div>
            </div>
            <h2 style={styles.title}>Searching for Therapist...</h2>
            <p style={styles.subtitle}>Matching you with someone specializing in {category}.</p>
            <button 
              onClick={() => { /* Add cancel logic */ onClose(); }}
              style={styles.cancelBtn}
            >
              Cancel Request
            </button>
          </div>
        )}

        {step === "active" && sessionId && (
          <LiveChat 
            sessionId={sessionId} 
            currentUser={{...user, role: 'user'}} 
            onEnd={onClose}
          />
        )}
      </motion.div>
    </motion.div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    backgroundColor: '#FFF7F1',
    borderRadius: '32px',
    padding: '40px',
    width: '90%',
    maxWidth: '500px',
    position: 'relative',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  closeBtn: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#888'
  },
  content: {
    textAlign: 'center'
  },
  title: {
    fontFamily: "'Syne', sans-serif",
    fontSize: '28px',
    fontWeight: '700',
    color: '#003049',
    marginBottom: '12px'
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '32px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  catCard: {
    backgroundColor: '#FFF',
    border: '2px solid #F1E0D0',
    borderRadius: '20px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    ':hover': {
      borderColor: '#F07167',
      backgroundColor: '#FFF1F0',
      transform: 'translateY(-4px)'
    }
  },
  catIcon: { fontSize: '32px' },
  catLabel: { fontWeight: '600', color: '#333' },
  loaderWrapper: {
    position: 'relative',
    width: '100px',
    height: '100px',
    margin: '0 auto 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  loaderIcon: { fontSize: '40px', zIndex: 2 },
  pulseRing: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor: '#F07167',
    opacity: 0.2,
    animation: 'pulse 2s infinite'
  },
  cancelBtn: {
    marginTop: '24px',
    background: 'none',
    border: '1px solid #D1D5DB',
    padding: '10px 20px',
    borderRadius: '12px',
    color: '#666',
    cursor: 'pointer'
  }
};
